import {Moment} from 'moment';
import {Link, useRouteMatch} from 'react-router-dom';
import {ScreenPaths} from '../../../../../../../enums';
import {GenericCollapsibleSidePanel} from '../../../../../../../components/generic-collapsible-side-panel/generic-collapsible-side-panel';
import {CalendarSideActivitiesSectorsFilter} from './calendar-side-activities-sectors-filter';
import {PlannerSideCalendar} from './planner-side-calendar';
import {FetchActivitiesSectorsAndGroups} from '../shared/use-fetch-activities-sectors-and-groups';
import {useOffice} from '../../../../../../../contexts/office/context';
import {Alarm, CalendarMonth} from '@mui/icons-material';
import {
  Box,
  Divider,
  ListItemButton,
  ListItemIcon,
  ListItemText,
  Stack,
  Tooltip,
} from '@mui/material';
import {useTranslation} from 'react-i18next';
import {translations} from '../../../../../../../i18n/translations';
import {useObserveViewport} from '../../../../../../../hooks';
import {useEffect} from 'react';

type PlannerSiderPanelProps = {
  entityId: string;
  remindersAvailable: boolean;
} & (
  | {showReminders: true}
  | {
      showReminders: false;
      date: Moment;
      onDateChange: (newValue: Moment) => void;
      activitiesSectorsAndGroupsData: FetchActivitiesSectorsAndGroups;
      onFilterActivitiesGroupsChange: (filters: string[]) => void;
    }
);

export function PlannerSiderPanel(props: PlannerSiderPanelProps) {
  const officeContext = useOffice();
  const {isRunningCloseToMobile} = useObserveViewport();
  const {t} = useTranslation();
  const collapsed = officeContext.state.sideNavigationDrawerCollapsed;
  const {plannerRoutePath, calendarPath, remindersPath} = getPlannerPaths(props.entityId);
  const calendarMatch = useRouteMatch({path: [plannerRoutePath, calendarPath], exact: true});

  useEffect(() => {
    officeContext.toggleSideNavigationMenuDrawer(isRunningCloseToMobile);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isRunningCloseToMobile]);

  return (
    <GenericCollapsibleSidePanel>
      {props.remindersAvailable && (
        <>
          {[
            {
              label: t(translations.role_permissions_section.calendar),
              icon: <CalendarMonth />,
              to: calendarPath,
            },
            {label: t(translations.reminders.my_title), icon: <Alarm />, to: remindersPath},
          ].map(({label, icon, to}, index) => (
            <Tooltip key={label} title={collapsed ? label : ''} placement="right">
              <ListItemButton
                component={Link}
                to={to}
                aria-label={label}
                selected={to === calendarPath ? !!calendarMatch : props.showReminders}
                sx={{
                  width: collapsed ? '40px' : '180px',
                  height: '40px',
                  mx: '11px',
                  mt: index > 0 ? '8px' : undefined,
                  px: collapsed ? 0 : '4px',
                  justifyContent: collapsed ? 'center' : undefined,
                  borderRadius: '5px',
                  color: 'primary.main',
                  '&.Mui-selected': {backgroundColor: 'primary.main', color: 'white'},
                  '&.Mui-selected:hover': {backgroundColor: 'primary.main'},
                }}
              >
                <ListItemIcon
                  sx={{minWidth: 'initial', mr: collapsed ? 0 : '6px', color: 'inherit'}}
                >
                  {icon}
                </ListItemIcon>
                {!collapsed && (
                  <ListItemText
                    primary={label}
                    primaryTypographyProps={{fontSize: 14, fontWeight: 500, noWrap: true}}
                  />
                )}
              </ListItemButton>
            </Tooltip>
          ))}
          {!collapsed && !props.showReminders && <Divider sx={{mt: '8px', mb: '16px'}} />}
        </>
      )}
      {!props.showReminders &&
        (!props.remindersAvailable && collapsed ? (
          <Stack alignItems="center">
            <ListItemButton
              sx={{
                margin: 0,
                padding: 0,
                width: '40px',
                height: '40px',
                borderRadius: '10%',
                display: 'flex',
                placeContent: 'center',
              }}
              onClick={() => {
                officeContext.toggleSideNavigationMenuDrawer(false);
              }}
            >
              <ListItemIcon
                sx={{
                  minWidth: 'initial',
                }}
              >
                <CalendarMonth color="primary" />
              </ListItemIcon>
            </ListItemButton>
          </Stack>
        ) : !collapsed ? (
          <Box>
            <PlannerSideCalendar value={props.date} onChange={props.onDateChange} />

            <CalendarSideActivitiesSectorsFilter
              activitiesSectorsAndGroupsData={props.activitiesSectorsAndGroupsData}
              onChange={props.onFilterActivitiesGroupsChange}
            />
          </Box>
        ) : null)}
    </GenericCollapsibleSidePanel>
  );
}

function getPlannerPaths(entityId: string) {
  const entityPath = `${ScreenPaths.Office}${ScreenPaths.Office_ProfessionalCaregiver}/${entityId}`;
  return {
    plannerRoutePath: `${entityPath}${ScreenPaths.Entity_Planner}`,
    calendarPath: `${entityPath}${ScreenPaths.Entity_Planner_Calendar}`,
    remindersPath: `${entityPath}${ScreenPaths.Entity_Planner_Reminders}`,
  };
}
